import { useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, y = 32, className = "", once = true, depth = false }) => (
  <motion.div
    initial={{ opacity: 0, y, ...(depth ? { rotateX: 14, scale: 0.96, transformPerspective: 900 } : {}) }}
    whileInView={{ opacity: 1, y: 0, ...(depth ? { rotateX: 0, scale: 1 } : {}) }}
    viewport={{ once, margin: "-70px" }}
    transition={{ duration: 0.9, delay, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

export const Tilt = ({ children, className = "", max = 6 }) => {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 160, damping: 16 });
  const sry = useSpring(ry, { stiffness: 160, damping: 16 });

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    ry.set(((e.clientX - rect.left) / rect.width - 0.5) * max);
    rx.set(-((e.clientY - rect.top) / rect.height - 0.5) * max);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: srx, rotateY: sry, transformPerspective: 1000 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const SectionHeading = ({ overline, title, description, dark = false, align = "left" }) => (
  <div className={align === "center" ? "text-center" : "text-left"} data-testid="section-heading">
    <Reveal>
      <p className="text-xs sm:text-sm uppercase tracking-[0.28em] text-gold-500 font-semibold mb-4">
        {overline}
      </p>
    </Reveal>
    <Reveal delay={0.08}>
      <h2 className={`font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight ${dark ? "text-white" : "text-navy-900"}`}>
        {title}
      </h2>
    </Reveal>
    {description && (
      <Reveal delay={0.16}>
        <p className={`mt-4 max-w-2xl text-sm sm:text-base leading-relaxed ${dark ? "text-white/70" : "text-gray-600"} ${align === "center" ? "mx-auto" : ""}`}>
          {description}
        </p>
      </Reveal>
    )}
  </div>
);
